import { useState, useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { contactsAPI, csvAPI, type CSVColumnMapping, type CSVAnalysisResult } from '../../services/api';
import { Button, Modal } from '../ui';
import { parseCSV, type ParsedCSV } from '../../utils/csvParser';

type ImportStep = 'upload' | 'analyzing' | 'mapping' | 'preview' | 'importing' | 'complete';

interface CSVImportModalProps {
  open: boolean;
  onClose: () => void;
  onImportComplete?: (importedCount: number) => void;
}

interface ImportResult {
  success: number;
  failed: number;
  skipped: number;
  errors: { row: number; message: string }[];
}

export function CSVImportModal({ open, onClose, onImportComplete }: CSVImportModalProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<ImportStep>('upload');
  const [dragActive, setDragActive] = useState(false);
  const [csvData, setCsvData] = useState<ParsedCSV | null>(null);
  const [useAI, setUseAI] = useState(true);
  const [aiAnalysis, setAiAnalysis] = useState<CSVAnalysisResult | null>(null);
  const [aiUsed, setAiUsed] = useState(false);
  const [mappings, setMappings] = useState<CSVColumnMapping[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Reset state when modal closes
  const handleClose = () => {
    setStep('upload');
    setCsvData(null);
    setUseAI(true);
    setAiAnalysis(null);
    setAiUsed(false);
    setMappings([]);
    setParseError(null);
    setImportResult(null);
    onClose();
  };

  // Handle file selection
  const handleFile = useCallback(async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setParseError('Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const content = e.target?.result as string;
      const result = parseCSV(content);

      if (!result.success || !result.data) {
        setParseError(result.errors?.[0]?.message || 'Failed to parse CSV');
        return;
      }

      setCsvData(result.data);
      setParseError(null);
      setStep('analyzing');

      // Run AI analysis
      try {
        const analysisResult = await csvAPI.analyze(
          result.data.headers,
          result.data.rows.slice(0, 5), // First 5 rows for analysis
          useAI
        );

        if (analysisResult.success) {
          setAiAnalysis(analysisResult.data.analysis);
          setAiUsed(analysisResult.data.ai_used);
          setMappings(analysisResult.data.analysis.mappings);
        }
      } catch (err) {
        console.error('AI analysis failed:', err);
        // Fall back to basic mappings
        setMappings(result.data.headers.map((header, index) => ({
          csv_column: header,
          csv_column_index: index,
          maps_to: [],
          transformation: 'direct' as const,
          confidence: 'low' as const,
          examples: [],
        })));
      }

      setStep('mapping');
    };

    reader.onerror = () => {
      setParseError('Failed to read file');
    };

    reader.readAsText(file);
  }, [useAI]);

  // Drag and drop handlers
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  // Update a single mapping
  const updateMapping = (index: number, field: string | null) => {
    setMappings(prev => prev.map((m, i) =>
      i === index
        ? { ...m, maps_to: field ? [field] : [] }
        : m
    ));
  };

  // Check if mapping is valid (has first_name + contact method)
  const isMappingValid = () => {
    const mappedFields = mappings.flatMap(m => m.maps_to);
    const hasName = mappedFields.includes('first_name');
    const hasContactMethod =
      mappedFields.includes('email') ||
      mappedFields.includes('phone') ||
      mappedFields.includes('address_street');
    return hasName && hasContactMethod;
  };

  // Get validation message
  const getValidationMessage = () => {
    const mappedFields = mappings.flatMap(m => m.maps_to);
    const hasName = mappedFields.includes('first_name');
    const hasContactMethod =
      mappedFields.includes('email') ||
      mappedFields.includes('phone') ||
      mappedFields.includes('address_street');

    if (!hasName && !hasContactMethod) {
      return 'Map "First Name" and at least one contact method (Email, Phone, or Address)';
    }
    if (!hasName) return 'Map a column to "First Name"';
    if (!hasContactMethod) return 'Map at least one contact method (Email, Phone, or Address)';
    return null;
  };

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async () => {
      if (!csvData) throw new Error('No CSV data');

      // First, transform the data using the API
      const transformResult = await csvAPI.transform(csvData.rows, mappings);

      if (!transformResult.success) {
        throw new Error('Transform failed');
      }

      const { contacts, skipped } = transformResult.data;
      const result: ImportResult = { success: 0, failed: 0, skipped, errors: [] };

      // Import each contact
      for (let i = 0; i < contacts.length; i++) {
        try {
          await contactsAPI.create(contacts[i]);
          result.success++;
        } catch (err) {
          result.failed++;
          result.errors.push({ row: i + 2, message: 'Failed to create contact' });
        }
      }

      return result;
    },
    onSuccess: (result) => {
      setImportResult(result);
      setStep('complete');
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['contacts', 'stats'] });

      // Set flag for post-import cleanup offer
      if (result.success > 0) {
        localStorage.setItem('ondeck-just-imported', 'true');
        localStorage.setItem('ondeck-last-import-count', String(result.success));
      }
    },
    onError: () => {
      setImportResult({ success: 0, failed: csvData?.rowCount || 0, skipped: 0, errors: [] });
      setStep('complete');
    },
  });

  const handleImport = () => {
    setStep('importing');
    importMutation.mutate();
  };

  // Get confidence badge color
  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
      case 'medium': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30';
      case 'low': return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30';
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900/30';
    }
  };

  // Available fields for mapping
  const AVAILABLE_FIELDS = [
    { key: 'first_name', label: 'First Name *' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'phone_secondary', label: 'Phone (Secondary)' },
    { key: 'company', label: 'Company' },
    { key: 'job_title', label: 'Job Title' },
    { key: 'stage', label: 'Stage' },
    { key: 'tags', label: 'Tags' },
    { key: 'source', label: 'Source' },
    { key: 'contact_type', label: 'Contact Type' },
    { key: 'address_street', label: 'Address (Street)' },
    { key: 'address_city', label: 'City' },
    { key: 'address_state', label: 'State' },
    { key: 'address_zip', label: 'ZIP Code' },
    { key: 'birthday', label: 'Birthday' },
    { key: 'notes', label: 'Notes' },
    { key: 'linkedin_url', label: 'LinkedIn' },
  ];

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Import Contacts from CSV"
      size="xl"
    >
      <div className="px-6 pb-6">
        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div>
            {/* AI Toggle */}
            <div className="mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useAI}
                  onChange={(e) => setUseAI(e.target.checked)}
                  className="mt-1 h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                />
                <div>
                  <span className="font-medium text-purple-800 dark:text-purple-300">
                    Use AI to analyze and clean up my data
                  </span>
                  <p className="text-sm text-purple-600 dark:text-purple-400 mt-1">
                    Your data will be processed by Google Gemini to intelligently detect fields,
                    parse names, normalize phone numbers, and map stages.
                  </p>
                </div>
              </label>
            </div>

            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
                dragActive
                  ? 'border-ondeck-500 bg-ondeck-50 dark:bg-ondeck-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-ondeck-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <div className="text-4xl mb-4">📄</div>
              <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Drop your CSV file here
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                or click to browse
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleInputChange}
                className="hidden"
              />
              <Button variant="outline" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                Select File
              </Button>
            </div>

            {parseError && (
              <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                {parseError}
              </div>
            )}

            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h4 className="font-medium text-gray-900 dark:text-white mb-2">Requirements</h4>
              <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                <li>• Each contact needs a <strong>First Name</strong></li>
                <li>• Plus at least one way to contact: <strong>Email</strong>, <strong>Phone</strong>, or <strong>Address</strong></li>
                <li>• First row should contain column headers</li>
              </ul>
            </div>
          </div>
        )}

        {/* Step: Analyzing */}
        {step === 'analyzing' && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              {useAI ? 'AI is analyzing your data...' : 'Analyzing columns...'}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Detecting fields and suggesting mappings
            </p>
          </div>
        )}

        {/* Step 2: Column Mapping */}
        {step === 'mapping' && csvData && (
          <div>
            {/* Quality Score */}
            {aiAnalysis && (
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {aiUsed ? (
                    <span className="text-purple-600 dark:text-purple-400 text-sm font-medium">
                      AI Analysis
                    </span>
                  ) : (
                    <span className="text-gray-500 dark:text-gray-400 text-sm">
                      Pattern Matching
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Quality:</span>
                  <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-ondeck-500 rounded-full"
                      style={{ width: `${(aiAnalysis.quality_score || 0.5) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {Math.round((aiAnalysis.quality_score || 0.5) * 100)}%
                  </span>
                </div>
              </div>
            )}

            {/* Warnings */}
            {aiAnalysis?.warnings && aiAnalysis.warnings.length > 0 && (
              <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <ul className="text-sm text-yellow-700 dark:text-yellow-400 space-y-1">
                  {aiAnalysis.warnings.map((warning, i) => (
                    <li key={i}>⚠️ {warning}</li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Found <strong>{csvData.rowCount}</strong> rows. Review the field mappings below:
            </p>

            <div className="max-h-80 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                      CSV Column
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                      Sample
                    </th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                      Map To
                    </th>
                    <th className="px-4 py-2 text-center text-sm font-medium text-gray-700 dark:text-gray-300 w-20">
                      Confidence
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {mappings.map((mapping, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {mapping.csv_column}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                        {csvData.rows[0]?.[index] || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={mapping.maps_to[0] || ''}
                          onChange={(e) => updateMapping(index, e.target.value || null)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-ondeck-500"
                        >
                          <option value="">-- Skip --</option>
                          {AVAILABLE_FIELDS.map(field => (
                            <option key={field.key} value={field.key}>
                              {field.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {mapping.maps_to.length > 0 && (
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getConfidenceColor(mapping.confidence)}`}>
                            {mapping.confidence}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Examples from AI */}
            {aiUsed && mappings.some(m => m.examples?.length > 0) && (
              <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <h4 className="text-sm font-medium text-purple-800 dark:text-purple-300 mb-2">
                  AI Transformations Preview
                </h4>
                <div className="space-y-2">
                  {mappings.filter(m => m.examples?.length > 0).slice(0, 3).map((mapping, i) => (
                    <div key={i} className="text-sm">
                      <span className="text-purple-600 dark:text-purple-400">{mapping.csv_column}:</span>
                      {mapping.examples.slice(0, 1).map((ex, j) => (
                        <span key={j} className="ml-2">
                          "{ex.input}" → {Object.entries(ex.output).map(([k, v]) => `${k}: "${v}"`).join(', ')}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Validation Message */}
            {!isMappingValid() && (
              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg text-yellow-700 dark:text-yellow-400 text-sm">
                {getValidationMessage()}
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setStep('upload')} className="flex-1">
                Back
              </Button>
              <Button onClick={() => setStep('preview')} disabled={!isMappingValid()} className="flex-1">
                Preview Import
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Preview */}
        {step === 'preview' && csvData && (
          <div>
            <div className="p-4 bg-ondeck-50 dark:bg-ondeck-900/30 rounded-lg mb-4">
              <div className="flex items-center gap-2 text-ondeck-800 dark:text-ondeck-300">
                <span className="text-xl">📊</span>
                <span className="font-medium">
                  Ready to import {csvData.rowCount} contacts
                </span>
              </div>
              <p className="text-sm text-ondeck-600 dark:text-ondeck-400 mt-1">
                Contacts without a first name or contact method will be skipped.
              </p>
            </div>

            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Mapped Fields</h4>
            <div className="flex flex-wrap gap-2 mb-4">
              {mappings.filter(m => m.maps_to.length > 0).map((m, i) => (
                <span key={i} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm text-gray-700 dark:text-gray-300">
                  {m.csv_column} → {m.maps_to.join(', ')}
                </span>
              ))}
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="outline" onClick={() => setStep('mapping')} className="flex-1">
                Back
              </Button>
              <Button onClick={handleImport} className="flex-1">
                Import Contacts
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Importing */}
        {step === 'importing' && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ondeck-600 mx-auto mb-4"></div>
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              Importing contacts...
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              This may take a moment
            </p>
          </div>
        )}

        {/* Step 5: Complete */}
        {step === 'complete' && importResult && (
          <div>
            <div className="text-center py-8">
              <div className="text-5xl mb-4">
                {importResult.failed === 0 ? '🎉' : importResult.success > 0 ? '⚠️' : '❌'}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Import Complete
              </h3>
              <div className="flex justify-center gap-6 text-lg">
                <div className="text-green-600 dark:text-green-400">
                  <span className="font-bold">{importResult.success}</span> imported
                </div>
                {importResult.skipped > 0 && (
                  <div className="text-gray-500 dark:text-gray-400">
                    <span className="font-bold">{importResult.skipped}</span> skipped
                  </div>
                )}
                {importResult.failed > 0 && (
                  <div className="text-red-600 dark:text-red-400">
                    <span className="font-bold">{importResult.failed}</span> failed
                  </div>
                )}
              </div>
            </div>

            {importResult.errors.length > 0 && (
              <div className="max-h-40 overflow-y-auto border border-red-200 dark:border-red-800 rounded-lg p-3 bg-red-50 dark:bg-red-900/30 mb-6">
                <h4 className="font-medium text-red-700 dark:text-red-400 mb-2">Errors:</h4>
                <ul className="text-sm text-red-600 dark:text-red-400 space-y-1">
                  {importResult.errors.slice(0, 10).map((error, i) => (
                    <li key={i}>Row {error.row}: {error.message}</li>
                  ))}
                  {importResult.errors.length > 10 && (
                    <li>... and {importResult.errors.length - 10} more</li>
                  )}
                </ul>
              </div>
            )}

            <div className="flex gap-3">
              <Button onClick={handleClose} variant="outline" className="flex-1">
                Done
              </Button>
              {importResult.success > 0 && (
                <Button
                  onClick={() => {
                    onImportComplete?.(importResult.success);
                    handleClose();
                  }}
                  className="flex-1"
                >
                  Sort Contacts
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
